<script lang="ts" module>
	import Link from '#components/link.svelte'
	import Pagination from '#components/pagination.svelte'
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Latest, News, Event } from '#library/components.schema.build.ts'
	import CardNews from '#components/card_news.svelte'
	import embla from 'embla-carousel-svelte'
	import type { EmblaCarouselType } from 'embla-carousel'
	import { format_date } from 'kitto'
	import CardEvent from '#components/card_event.svelte'
	import type { ISbStories, ISbStoriesParams } from '@storyblok/svelte'
	import * as _ from 'radashi'

	export const load: Load = async ({ story, api, version }) => {
		if (!('blocks' in story.content)) return

		const latest_blocks = story.content.blocks?.filter(b => b.component === 'latest')

		if (!latest_blocks?.length) return

		// the three queries are independent of each other, and so are the blocks.
		// They ran one after another, so the cost was their sum rather than the
		// slowest of them.
		await Promise.all(
			latest_blocks.map(async latest_block => {
				const types = 'types' in latest_block && latest_block.types

				// skips this block only. This used to `return` out of the whole load,
				// so one untyped block stopped every later one from loading at all.
				if (!types) return

				const per_page = Number(latest_block.count)

				async function stories<T>(params: ISbStoriesParams) {
					const response = (await api.get('cdn/stories', {
						version,
						per_page,
						resolve_links: 'url',
						...params
					})) as ISbStories<T>

					return response.data.stories
				}

				const [news, thoughts, events] = await Promise.all([
					types.includes('news')
						? stories<News>({
								excluding_slugs: 'journal/thought-leadership/*,journal/podcast',
								starts_with: 'journal/',
								is_startpage: false,
								sort_by: 'first_published_at:desc'
							})
						: undefined,
					types.includes('thoughts')
						? stories<News>({
								starts_with: 'journal/thought-leadership/',
								is_startpage: false,
								sort_by: 'first_published_at:desc'
							})
						: undefined,
					types.includes('events')
						? stories<Event>({
								content_type: 'event',
								sort_by: 'sort_by_date',
								filter_query: { exclude_from_latest: { not_in: true } }
							})
						: undefined
				])

				if (news) latest_block.news = _.cluster(news, 3)
				if (thoughts) latest_block.thoughts = _.cluster(thoughts, 3)
				if (events) latest_block.events = _.cluster(events, 3)
			})
		)
	}
</script>

<script lang="ts">
	interface Props {
		blok: Blok<Latest>
	}

	type LatestType = Exclude<NonNullable<Latest['types']>[number], ''>

	let { blok }: Props = $props()
	let on = $derived(blok?.types?.[0] as LatestType)
	let active: Record<LatestType, number> = $state({
		events: 0,
		news: 0,
		thoughts: 0
	})

	let embla_api: EmblaCarouselType | undefined = $state()

	const items_length = $derived.by(() => {
		if (on === 'events') return blok.events?.flat().length ?? 0
		if (on === 'news') return blok.news?.flat().length ?? 0
		if (on === 'thoughts') return blok.thoughts?.flat().length ?? 0
		return 0
	})

	function init(event: CustomEvent) {
		embla_api = event.detail
		if (!embla_api) return

		embla_api.on('select', () => {
			active[on] = embla_api?.selectedScrollSnap() ?? 0
		})
	}

	type EmbleOptions = Parameters<typeof embla>[1]

	const embla_options: EmbleOptions = {
		options: { align: 'start' },
		plugins: []
	}

	const paginations: Record<LatestType, number> = {
		events: 0,
		news: 0,
		thoughts: 0
	}

	function change_to(type: LatestType) {
		on = type
		embla_api?.scrollTo(paginations[type])
	}

	function link(type: LatestType) {
		if (type === 'events') return '/whats-on'
		if (type === 'news') return '/journal/news'
		if (type === 'thoughts') return '/journal/thought-leadership'
		return '/'
	}
</script>

<section class="latest {blok.theme || 'theme-rose-on-white'}" {...attrs(blok)}>
	<div class="inner contain">
		{#if blok?.types?.length && blok.types.length > 1}
			<nav class="tabs">
				{#each blok.types as type (type)}
					{@const names = {
						events: 'What’s on',
						news: 'Our news',
						thoughts: 'Thought leadership'
					}}
					<div class="inline">
						<button class="tab" class:on={on === type} onclick={() => type && change_to(type)}>
							{names[type as keyof typeof names]}
						</button>
						{#if on === type}
							<Link href={link(type)} text="View all {type}" />
						{/if}
					</div>
				{/each}
			</nav>
			<div class="xlink">
				{#each blok.types as type (type)}
					{#if on === type}
						<Link href={link(type)} text="View all {type}" />
					{/if}
				{/each}
			</div>
		{/if}
		{#if blok?.types?.length === 1}
			<div class="heading">
				<div class="content">
					<div class="title">{blok.title}</div>
					<div class="subtitle">{blok.subtitle}</div>
				</div>
				{#if blok.link?.url && blok.link?.text}
					<Link href={blok.link.url} text={blok.link.text} />
				{/if}
			</div>
		{/if}
	</div>
	<div class="carousel" use:embla={embla_options} onemblaInit={init}>
		<div class="slides">
			{#if on === 'events'}
				{#each blok.events ?? [] as group, i (i)}
					<div class="slide">
						<div class="group contain">
							{#each group as event, j (j)}
								<div class="item event">
									<CardEvent {event} view="date" />
								</div>
							{/each}
						</div>
					</div>
				{/each}
			{:else if on === 'news'}
				{#each blok.news ?? [] as group, i (i)}
					<div class="slide">
						<div class="group contain">
							{#each group as news, j (j)}
								<div class="item">
									<CardNews
										link={news.full_slug}
										image={news.content.image}
										suptitle={news.first_published_at
											? format_date('{ddd}, {DD} {MMM} {YYYY}', news.first_published_at)
											: undefined}
										title={news.name}
										subtitle={news.content.metatags?.description ?? ''}
										caption={news.content.component}
										type={news.content.component}
									/>
								</div>
							{/each}
						</div>
					</div>
				{/each}
			{:else if on === 'thoughts'}
				{#each blok.thoughts ?? [] as group, i (i)}
					<div class="slide">
						<div class="group contain">
							{#each group as thought, j (j)}
								<div class="item">
									<!-- <div class="item fill"> -->
									<!-- Will asked for CardThought to be replaced with CardNews -->
									<CardNews
										link={thought.full_slug}
										image={thought.content.image}
										suptitle={thought.first_published_at
											? format_date('{ddd}, {DD} {MMM} {YYYY}', thought.first_published_at)
											: undefined}
										title={thought.name}
										subtitle={thought.content.metatags?.description ?? ''}
										caption="Thought Leadership"
										theme="theme-lupton-on-pollen"
									/>
								</div>
							{/each}
						</div>
					</div>
				{/each}
			{/if}
		</div>
	</div>
	{#if items_length > 1}
		{@const items = Math.ceil(items_length / 3)}
		{#if items > 1}
			<div class="pagi">
				{#key items_length}
					<Pagination
						onprev={() => embla_api?.scrollPrev()}
						onnext={() => embla_api?.scrollNext()}
						onpage={page => embla_api?.scrollTo(page)}
						{items}
						active={active[on]}
					/>
				{/key}
			</div>
		{/if}
	{/if}
</section>

<style lang="css">
	.latest {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.inner {
		align-items: start;
		display: grid;
		gap: 2rem;

		@media (--from-mobile) {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	.content {
		display: contents;

		@media (--from-mobile) {
			display: block;
			grid-column: 1 / 3;
		}
	}

	.tabs {
		align-items: start;
		display: flex;
		gap: 1.75rem;
		grid-column: 1 / -1;
		margin-inline: -6vw;
		padding-inline: 6vw;
		overflow-x: auto;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		@media (--from-mobile) {
			align-items: unset;
			display: grid;
			gap: 0.25rem;
			margin-inline: -5vw;
			padding-inline: 5vw;
		}
	}

	.inline {
		display: grid;

		@media (--from-mobile) {
			align-items: start;
			gap: 2rem;
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		@media (--until-mobile) {
			:global(.link) {
				display: none;
			}
		}
	}

	.xlink {
		@media (--from-mobile) {
			display: none;
		}
	}

	.tab {
		color: var(--c-midground);
		font: 600 var(--f-h3);
		height: 3.75rem;
		letter-spacing: var(--ls-h3);
		text-align: start;
		white-space: nowrap;

		@media (--from-mobile) {
			grid-column: 1 / 3;
		}

		&:hover,
		&.on {
			color: var(--c-foreground);
			text-decoration: underline;
		}
	}

	.heading {
		align-items: start;
		display: grid;
		gap: 0.5rem;
		grid-column: 1 / -1;

		@media (--from-mobile) {
			gap: 2rem;
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-wrap: balance;
	}

	.subtitle {
		color: var(--c-midground);
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
		margin-block-start: 1rem;
		order: 1;
		text-wrap: balance;

		@media (--from-mobile) {
			margin-block-start: 2.5rem;
			order: unset;
		}
	}

	.carousel {
		grid-column: 1 / -1;
		overflow: clip;
		cursor: grab;
		user-select: none;
	}

	.slides {
		margin-block: 2rem 0;

		display: flex;
		@media (--from-mobile) {
			gap: 2rem;
		}
	}

	.slide {
		flex: 1 0 100%;
	}

	.group {
		display: flex;
		gap: 2rem;
		flex: 1 0 100%;
		flex-wrap: wrap;

		@media (--until-mobile) {
			margin-block-end: 2rem;
		}
	}

	.item {
		display: flex;
		gap: 4rem;
		flex-grow: 1;
		/* margin-block-start: 1.5rem; */

		&.fill {
			flex: 1 0 100%;
		}

		@media (--from-mobile) {
			display: block;
			flex: 1 0 calc(33.3333% - (4rem / 3));
			/* margin-block-start: 2rem; */
		}
	}

	.event {
		border-right: 1px solid var(--c-gimson);
		padding-inline-end: 2rem;

		&:last-child {
			border-right: none;
			padding-inline-end: 0;
		}
	}

	.suptitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 1.75rem;
		text-transform: uppercase;
	}

	.caption {
		background-color: var(--c-rose);
		color: var(--c-white);
		font: 500 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		padding: 0.375rem 1rem;
		text-align: center;
		text-transform: uppercase;
	}

	.pagi {
		display: flex;
		grid-column: 1 / -1;
		justify-content: center;
		margin-block-start: 1rem;

		@media (--until-mobile) {
			margin-block-start: 0.5rem;
			margin-inline-start: auto;
			padding-inline: 6vw;
		}
	}
</style>
